/**
 * R&D · Sampling 360 Workspace (Wave D).
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, TextInput } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { DocumentActions } from "../../components/DocumentActions";
import { SAMPLING_BATCHES, SamplingBatch, RND_PRODUCTS, BLIND_PANELS, FORMULAS, RND_DECISIONS } from "../../mocks/rndData";

const STATUS_META: Record<SamplingBatch["status"], { bg: string; fg: string }> = {
  dispatched:  { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"  },
  "in-progress":{ bg: "var(--ph-ai-tint)",     fg: "var(--ph-ai)"       },
  returned:    { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  complete:    { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"  },
};

function pname(id: string) { return RND_PRODUCTS.find((p) => p.id === id)?.name ?? id; }

type SmpTab = "overview" | "sample" | "batch" | "sentTo" | "courier" | "results" | "decision" | "timeline" | "audit";

export default function Sampling() {
  const [tab, setTab] = useState<SamplingBatch["status"] | "all">("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<SamplingBatch | null>(null);
  const [wsTab, setWsTab] = useState<SmpTab>("overview");

  const filtered = useMemo(() => SAMPLING_BATCHES.filter((b) =>
    (tab === "all" || b.status === tab) &&
    (!q || b.code.toLowerCase().includes(q.toLowerCase()) || pname(b.productId).toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);

  const drill = useMemo(() => {
    if (!selected) return null;
    const panel   = BLIND_PANELS.find((p) => p.productId === selected.productId && p.version === selected.version) || BLIND_PANELS.find((p) => p.productId === selected.productId);
    const formula = FORMULAS.find((f) => f.productId === selected.productId && f.version === selected.version) || FORMULAS.find((f) => f.productId === selected.productId);
    const decision = RND_DECISIONS.find((d) => d.productId === selected.productId);
    return { panel, formula, decision };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="rd-sampling">
      <Card testId="rd-sm-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search sampling batches…" testId="rd-sm-search" />
        <div className="mt-4">
          <TabBar<SamplingBatch["status"] | "all">
            testId="rd-sm-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All"         },
              { key: "dispatched",  label: "Dispatched"  },
              { key: "in-progress", label: "In progress" },
              { key: "returned",    label: "Returned"    },
              { key: "complete",    label: "Complete"    },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="rd-sm-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Batches · click to drill" title={`${filtered.length} sampling batch${filtered.length === 1 ? "" : "es"}`} />
        </div>
        <div className="p-2">
          {filtered.map((b) => {
            const m = STATUS_META[b.status];
            const returned = Math.round((b.responsesIn / Math.max(1, b.responsesTotal)) * 100);
            return (
              <ListRow key={b.id} testId={`rd-sm-${b.id}`}
                onClick={() => { setSelected(b); setWsTab("overview"); }}
                label={<span className="inline-flex items-center gap-2">{b.code} · {b.version}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{b.status}</span>
                </span>}
                sublabel={<>{pname(b.productId)} · {b.panel} · {b.units} units · sent {b.sentRelative} · returns {b.responsesIn}/{b.responsesTotal} ({returned}%)</>}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Sampling Batch 360" testId="rd-sm-drawer" width={720}>
        {selected && drill && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: STATUS_META[selected.status].fg }}>{selected.status}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.code} · {selected.version}</div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<SmpTab>
                testId="rd-sm-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",  label: "Overview"  },
                  { value: "sample",    label: "Sample"    },
                  { value: "batch",     label: "Batch"     },
                  { value: "sentTo",    label: "Sent to"   },
                  { value: "courier",   label: "Courier"   },
                  { value: "results",   label: "Results"   },
                  { value: "decision",  label: "Decision"  },
                  { value: "timeline",  label: "Timeline"  },
                  { value: "audit",     label: "Audit"     },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="rd-sm-ws-overview">
                <Field label="Code"    inline><span className="text-sm font-mono">{selected.code}</span></Field>
                <Field label="Version" inline><span className="text-sm">{selected.version}</span></Field>
                <Field label="Product" inline><span className="text-sm">{pname(selected.productId)}</span></Field>
                <Field label="Units"   inline><span className="text-sm tabular-nums">{selected.units}</span></Field>
                <Field label="Panel"   inline><span className="text-sm">{selected.panel}</span></Field>
                <Field label="Returns" inline><span className="text-sm tabular-nums">{selected.responsesIn} / {selected.responsesTotal}</span></Field>
              </div>
            )}

            {wsTab === "sample" && (
              <div data-testid="rd-sm-ws-sample">
                <Field label="Product"  inline><span className="text-sm">{pname(selected.productId)}</span></Field>
                <Field label="Version"  inline><span className="text-sm">{selected.version}</span></Field>
                <Field label="Concentration"   inline><span className="text-sm">{drill.formula?.totalPct ?? "—"}%</span></Field>
              </div>
            )}

            {wsTab === "batch" && (
              <div data-testid="rd-sm-ws-batch">
                <Field label="Units"    inline><span className="text-sm tabular-nums">{selected.units}</span></Field>
                <Field label="Formula"  inline><span className="text-sm font-mono">{drill.formula?.id ?? "—"}</span></Field>
                <Field label="Cost / L" inline><span className="text-sm tabular-nums">EGP {drill.formula?.costEGPPerLiter?.toLocaleString() ?? "—"}</span></Field>
              </div>
            )}

            {wsTab === "sentTo" && (
              <div data-testid="rd-sm-ws-sent"><Field label="Panel" inline><span className="text-sm">{selected.panel}</span></Field></div>
            )}

            {wsTab === "courier" && (
              <div data-testid="rd-sm-ws-courier">
                <Field label="Sent"     inline><span className="text-sm">{selected.sentRelative}</span></Field>
                <Field label="Status"   inline><span className="text-sm">{selected.status}</span></Field>
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Courier + tracking numbers surface once dispatched.</div>
              </div>
            )}

            {wsTab === "results" && (
              <div data-testid="rd-sm-ws-results">
                {drill.panel ? (
                  <>
                    <Field label="Overall"      inline><span className="text-sm tabular-nums">{drill.panel.overall.toFixed(1)}/10</span></Field>
                    <Field label="Longevity"    inline><span className="text-sm tabular-nums">{drill.panel.longevity.toFixed(1)}</span></Field>
                    <Field label="Sillage"      inline><span className="text-sm tabular-nums">{drill.panel.sillage.toFixed(1)}</span></Field>
                    <Field label="Wearability"  inline><span className="text-sm tabular-nums">{drill.panel.wearability.toFixed(1)}</span></Field>
                    <Field label="Recommendation" inline><span className="text-sm font-semibold">{drill.panel.recommendation}</span></Field>
                    <Field label="Top comment"><span className="text-sm">{drill.panel.topComment}</span></Field>
                  </>
                ) : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Panel results pending.</div>}
              </div>
            )}

            {wsTab === "decision" && (
              <div data-testid="rd-sm-ws-decision">
                {drill.decision ? (
                  <>
                    <Field label="Decision"   inline><span className="text-sm font-semibold">{drill.decision.decision}</span></Field>
                    <Field label="Decided by" inline><span className="text-sm">{drill.decision.decidedBy}</span></Field>
                    <Field label="When"       inline><span className="text-sm">{drill.decision.dateRelative}</span></Field>
                    <Field label="Budget impact" inline><span className="text-sm tabular-nums">EGP {drill.decision.budgetImpactEGP.toLocaleString()}</span></Field>
                    <Field label="Rationale"><span className="text-sm">{drill.decision.rationale}</span></Field>
                  </>
                ) : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No decision recorded yet.</div>}
              </div>
            )}

            {wsTab === "timeline" && (
              <div data-testid="rd-sm-ws-timeline">
                <Field label="Sent"    inline><span className="text-sm">{selected.sentRelative}</span></Field>
                <Field label="Status"  inline><span className="text-sm">{selected.status}</span></Field>
                <AuditTrail collection="sampling_batches" recordId={selected.id} testId="rd-sm-timeline-audit" />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="rd-sm-ws-audit">
                <AuditTrail collection="sampling_batches" recordId={selected.id} testId="rd-sm-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
